(ns ui.editor2.modal-music
  "Editor2 music modal — pure Replicant. The modal :render draws the
   music UI (storrito.editor.music-modal) directly from the store; the
   interactive state lives under `[:ui/modal :music]` and every mutation
   (audio playback, catalog search, section drag, select, confirm) goes
   through the registered `:music/*` action handlers below, which
   receive the store. No Reagent, no nested render."
  (:require [storrito.editor.music-modal :as music-modal]
            [storrito.editor.music-modal.preview :as preview]
            [storrito.instagram.story.editor.music-search :as music.search]
            [parts.replicant.query :as query]
            [ui.editor2.common :as common]
            [datascript.core :as ds]
            [sv.core.async :refer-macros [<?]])
  (:require-macros [cljs.core.async.macros :refer [go]]))

(def ^:private mp [:ui/modal :music])

(def modal-id
  :modals/editor2-music-modal)

(defn- mget [store] (get-in @store mp))

(defn- mupdate!
  "Updates the music state, but only while the music modal is open. The
   audio events, the progress interval and the track search also call
   back after the modal was closed: a write would then bring `:ui/modal`
   back without its `:render`, an empty modal that reappears on every
   callback and thereby cannot be closed."
  [store f & args]
  (swap! store
         (fn [state]
           (if (= modal-id (get-in state [:ui/modal :modal-id]))
             (apply update-in state mp f args)
             state))))

(defn- massoc! [store ks v] (mupdate! store assoc-in ks v))

(defn get-existing-music-sticker
  "The existing music sticker asset if one exists (Instagram supports
   only one music sticker per story)."
  [db]
  (when-let [asset-id (first (ds/q '[:find [?asset ...]
                                     :where
                                     [?asset :asset/type :music-sticker]
                                     [_ :clip/asset ?asset]]
                                   db))]
    (ds/entity db asset-id)))

(defn slide-duration-ms
  "Duration of the first slide, which the music section length follows."
  [db]
  (or (some-> (common/slide-clips db)
              (first)
              (:slide/duration))
      15000))

;; --- audio playback (operates on the store) ---

(defn- current-audio?
  [store audio]
  (identical? audio (get-in @store (conj mp :preview :audio))))

(defn- silence!
  [audio]
  (set! (.-onloadedmetadata audio) nil)
  (set! (.-ontimeupdate audio) nil)
  (set! (.-onended audio) nil)
  (.pause audio)
  ;; Abort any pending loading, so a still-loading audio does not
  ;; start playing after it was stopped:
  (set! (.-src audio) ""))

(defn- stop-preview!
  [store]
  (let [{:keys [audio interval-id]} (get-in @store (conj mp :preview))]
    (when interval-id
      (js/clearInterval interval-id))
    (when audio
      (silence! audio)))
  (mupdate! store dissoc :preview :preview-progress-ms))

(defn- start-preview!
  "Loops a `preview-window-ms` window from the song's first highlight."
  [store track]
  (when (seq (:progressive_download_url track))
    (let [audio (js/Audio. (:progressive_download_url track))
          highlight-ms (or (first (:highlight_start_times_in_ms track))
                           0)
          start-s (/ highlight-ms 1000)
          end-s (/ (+ highlight-ms preview/preview-window-ms) 1000)]
      (set! (.-onloadedmetadata audio)
            (fn [] (set! (.-currentTime audio) start-s)))
      (set! (.-ontimeupdate audio)
            (fn []
              (cond
                ;; The music state went away without a `stop-preview!`
                ;; (e.g. a navigation), nobody could stop this audio:
                (not (current-audio? store audio))
                (silence! audio)

                (>= (.-currentTime audio) end-s)
                (set! (.-currentTime audio) start-s))))
      (set! (.-onended audio)
            (fn []
              (set! (.-currentTime audio) start-s)
              (-> (.play audio)
                  (.catch (fn [_e] nil)))))
      (-> (.play audio)
          (.catch (fn [e]
                    (js/console.warn "Music preview failed:" e)
                    (mupdate! store dissoc :preview))))
      (massoc! store [:preview] {:track-id (:id track)
                                 :audio audio
                                 :playing? true}))))

(defn- toggle-player-playback!
  [store]
  (let [{:keys [audio playing?]} (get-in @store (conj mp :preview))]
    (cond
      (nil? audio)
      (start-preview! store (get-in @store (conj mp :player-track)))

      playing?
      (do (.pause audio)
          (massoc! store [:preview :playing?] false))

      :else
      (do (-> (.play audio)
              (.catch (fn [_e] nil)))
          (massoc! store [:preview :playing?] true)))))

(defn- load-player-track!
  [store track]
  (if (= (:id track)
         (:id (get-in @store (conj mp :player-track))))
    (toggle-player-playback! store)
    (do
      (stop-preview! store)
      (massoc! store [:player-track] track)
      (start-preview! store track))))

(defn- priming-shift-s
  "The media element does not apply the MP4 edit list that trims the AAC
   priming, while Web Audio and ffmpeg do, so the element runs ahead by
   the priming duration. The shift keeps the preview aligned."
  [audio track]
  (let [metadata-s (/ (:duration_in_ms track 0) 1000)
        duration-s (.-duration audio)]
    (if (and (js/isFinite duration-s)
             (pos? metadata-s))
      (-> (- duration-s metadata-s)
          (max 0)
          (min 0.5))
      0)))

(defn- start-section-loop!
  "Auto-plays the selected song section in a loop, tracking the position
   in :preview-progress-ms for the progress fill."
  [store]
  (let [{:keys [track start-ms section-ms]} (get-in @store (conj mp :selected))]
    (when (seq (:progressive_download_url track))
      (let [audio (js/Audio. (:progressive_download_url track))
            start-s (/ start-ms 1000)
            end-s (/ (+ start-ms section-ms) 1000)
            shift-s (fn []
                      (get-in @store (conj mp :preview :shift-s) 0))]
        (set! (.-onloadedmetadata audio)
              (fn []
                (when (current-audio? store audio)
                  (massoc! store [:preview :shift-s]
                           (priming-shift-s audio track))
                  (set! (.-currentTime audio) (+ start-s (shift-s)))
                  (-> (.play audio)
                      (.catch (fn [e]
                                (js/console.warn "Music preview failed:" e)
                                (stop-preview! store)))))))
        (set! (.-ontimeupdate audio)
              (fn []
                (when (>= (.-currentTime audio) (+ end-s (shift-s)))
                  (set! (.-currentTime audio) (+ start-s (shift-s))))))
        (set! (.-onended audio)
              (fn []
                (when (current-audio? store audio)
                  (set! (.-currentTime audio) (+ start-s (shift-s)))
                  ;; A `stop-preview!` right after the section ended pauses
                  ;; the pending play(), which rejects with an AbortError
                  ;; (STORRITO-V2-M6):
                  (-> (.play audio)
                      (.catch (fn [_e] nil))))))
        (let [interval-id (volatile! nil)]
          (vreset! interval-id
                   (js/setInterval
                     (fn []
                       (if (current-audio? store audio)
                         (massoc! store [:preview-progress-ms]
                                  (* 1000 (- (.-currentTime audio)
                                             (shift-s))))
                         ;; The music state went away without a
                         ;; `stop-preview!` (e.g. a navigation):
                         (do (js/clearInterval @interval-id)
                             (silence! audio))))
                     100))
          (massoc! store [:preview]
                   {:track-id (:id track)
                    :audio audio
                    :interval-id @interval-id}))))))

(defn- restart-section-loop!
  [store]
  (stop-preview! store)
  (start-section-loop! store))

;; --- catalog search ---

(defn- do-load-tracks!
  [store request]
  (go
    (mupdate! store
              (fn [m]
                (-> m
                    (assoc :searching? true
                           :tracks-request request)
                    (dissoc :tracks :error-message))))
    (let [still-current? (fn []
                           (= request (:tracks-request (mget store))))
          fail! (fn []
                  (when (still-current?)
                    (massoc! store [:error-message]
                             "Sorry, the song search failed. Please try again.")))]
      (try
        (let [result (<? (music.search/search-tracks request))]
          (when (still-current?)
            (if (:tracks result)
              (mupdate! store merge result)
              (fail!))))
        (catch js/Error _e
          (fail!))
        (finally
          (when (still-current?)
            (mupdate! store dissoc :searching?)))))))

(defn- selected-account-id
  [music]
  (or (:account-id music)
      (-> (get-in music [:instagram-data :instagram.users])
          first
          :instagram.user/id
          str)))

;; --- select / confirm ---

(defn- select-track!
  [store track]
  (stop-preview! store)
  (let [music (mget store)
        section-ms (preview/default-section-ms music track)]
    (massoc! store [:selected]
             {:track track
              :section-ms section-ms
              :start-ms (preview/clamp-start-ms
                          track
                          section-ms
                          (or (first (:highlight_start_times_in_ms track))
                              0))})))

(defn- confirm!
  [store event-handler]
  (let [state @store
        music (get-in state mp)
        params (get-in state [:ui/modal :params])
        db (get-in state [:editor2 :db])
        is-editing? (some? (:asset/uuid params))
        existing (when-not is-editing?
                   (get-existing-music-sticker db))
        effective (if existing
                    (assoc params
                           :asset/uuid (:asset/uuid existing)
                           :db/id (:db/id existing)
                           :asset/data (:asset/data existing))
                    params)
        {:keys [track start-ms section-ms]} (:selected music)]
    (stop-preview! store)
    (event-handler
      {}
      [[:editor2/add-music-sticker
        {:track track
         :browse-session-id (:browse_session_id music)
         :alacorn-session-id (:alacorn_session_id music)
         :audio-start-ms (js/Math.round start-ms)
         :audio-duration-ms (js/Math.round section-ms)
         :existing (select-keys effective [:asset/uuid :db/id])}]
       [:ui/modal-close]])))

;; --- section drag ---
;;
;; Mirrors the editor2 timeline drag (ui.editor2.actions/drag-start-handler):
;; a single `:music/drag-start` action installs document-level move/up
;; listeners, and each move swaps the store directly (no per-move action
;; dispatch, so it stays snappy through the normal render). The bar's box
;; is captured once at grab time to convert clientX to a song position.

(defn- event-song-ms
  [drag client-x]
  (let [{:keys [bar-left bar-width duration]} drag]
    (* (/ (- client-x bar-left) bar-width) duration)))

(defn- drag-move!
  [store evt]
  (when-let [drag (get-in @store (conj mp :drag))]
    (let [ms (event-song-ms drag (.-clientX evt))
          sel (get-in @store (conj mp :selected))
          nsel (preview/drag-selection (:mode drag) sel (mget store)
                                       ms (:anchor-ms drag))]
      (mupdate! store update :selected merge nsel))))

(defn- drag-start-handler
  [{:keys [store action] :as params}]
  (let [evt (:replicant/js-event params)
        request-mode (:mode (second action))
        bar (.closest (.-target evt) ".js-section-bar")
        rect (.getBoundingClientRect bar)
        bar-left (.-left rect)
        bar-width (max 1 (.-width rect))
        {:keys [track start-ms section-ms]} (get-in @store (conj mp :selected))
        duration (max 1 (:duration_in_ms track))
        ms (* (/ (- (.-clientX evt) bar-left) bar-width) duration)
        ;; :bar (clicking the empty bar) centers the window on the cursor
        ;; and then drags it; :window grabs the window where it was
        ;; clicked; the resize modes drag one edge:
        [mode anchor-ms start0]
        (case request-mode
          :bar [:move (/ section-ms 2)
                (preview/clamp-start-ms track section-ms (- ms (/ section-ms 2)))]
          :window [:move (- ms start-ms) start-ms]
          :resize-left [:resize-left nil start-ms]
          :resize-right [:resize-right nil start-ms])
        mousemove (fn [e] (drag-move! store e))]
    ;; Silence the preview so its progress interval does not re-render
    ;; mid-drag, then seed the drag descriptor (and, for :bar, the
    ;; centered start):
    (stop-preview! store)
    (mupdate! store
              (fn [m]
                (-> m
                    (assoc :drag {:mode mode
                                  :anchor-ms anchor-ms
                                  :bar-left bar-left
                                  :bar-width bar-width
                                  :duration duration})
                    (assoc-in [:selected :start-ms] start0))))
    (.addEventListener js/document "mousemove" mousemove)
    (.addEventListener js/document "mouseup"
                       (fn self [_]
                         (.removeEventListener js/document "mousemove" mousemove)
                         (.removeEventListener js/document "mouseup" self)
                         (mupdate! store dissoc :drag)
                         (restart-section-loop! store)))))

;; --- init ---

(defn- init!
  [store]
  (let [state @store
        params (get-in state [:ui/modal :params])
        db (get-in state [:editor2 :db])
        ig-data (query/get-result state {:query/kind :query/ig-account-data})
        is-editing? (some? (:asset/uuid params))
        existing (when-not is-editing?
                   (get-existing-music-sticker db))
        effective (if existing
                    (assoc params
                           :asset/uuid (:asset/uuid existing)
                           :db/id (:db/id existing)
                           :asset/data (:asset/data existing))
                    params)
        data (:asset/data effective)
        section-length (slide-duration-ms db)]
    (swap! store assoc-in mp
           (cond-> {:instagram-data (:instagram-data ig-data)
                    :source :editor2
                    :section-length-ms section-length}
             ;; Editing an existing music sticker: start directly in the
             ;; section picker with the current track/section:
             (:track data)
             (assoc :editing? true
                    :selected {:track (:track data)
                               :section-ms (min (or (:audio-duration-ms data)
                                                    section-length)
                                                (:duration_in_ms (:track data)))
                               :start-ms (or (:audio-start-ms data)
                                             0)}
                    :browse_session_id (:browse-session-id data)
                    :alacorn_session_id (:alacorn-session-id data))))))

;; --- registered action handlers ---

(defn- h
  "Wraps a store-taking function as an action handler."
  [f]
  (fn [{:keys [store]}]
    (f store)))

(def music-register
  [{:ui.action/kind :music/init
    :ui.action/handler (h init!)}
   {:ui.action/kind :music/load-tracks
    :ui.action/handler (fn [{:keys [store action]}]
                         (let [[_ req] action
                               music (mget store)
                               account-id (selected-account-id music)
                               request (if (:query? req)
                                         {:query (:query-text music)
                                          :account-id account-id}
                                         (assoc (select-keys req [:category])
                                                :account-id account-id))]
                           (do-load-tracks! store request)))}
   {:ui.action/kind :music/load-player-track
    :ui.action/handler (fn [{:keys [store action]}]
                         (load-player-track! store (second action)))}
   {:ui.action/kind :music/toggle-playback
    :ui.action/handler (h toggle-player-playback!)}
   {:ui.action/kind :music/stop-preview
    :ui.action/handler (h stop-preview!)}
   {:ui.action/kind :music/select-track
    :ui.action/handler (fn [{:keys [store action]}]
                         (select-track! store (second action)))}
   {:ui.action/kind :music/back-to-search
    :ui.action/handler (fn [{:keys [store]}]
                         (stop-preview! store)
                         (mupdate! store dissoc :selected))}
   {:ui.action/kind :music/set-section
    :ui.action/handler (fn [{:keys [store action]}]
                         (let [option (second action)]
                           (mupdate! store update :selected
                                     (fn [{:keys [track start-ms] :as sel}]
                                       (assoc sel
                                              :section-ms option
                                              :start-ms (preview/clamp-start-ms
                                                          track option start-ms))))
                           (restart-section-loop! store)))}
   {:ui.action/kind :music/section-toggle-play
    :ui.action/handler (fn [{:keys [store]}]
                         (let [{:keys [track]} (get-in @store (conj mp :selected))]
                           (if (= (get-in @store (conj mp :preview :track-id))
                                  (:id track))
                             (stop-preview! store)
                             (start-section-loop! store))))}
   {:ui.action/kind :music/section-restart-loop
    :ui.action/handler (h restart-section-loop!)}
   {:ui.action/kind :music/drag-start
    :ui.action/handler drag-start-handler}
   {:ui.action/kind :music/seek-highlight
    :ui.action/handler (fn [{:keys [store action]}]
                         (let [ms (second action)
                               {:keys [track section-ms]} (get-in @store (conj mp :selected))]
                           (massoc! store [:selected :start-ms]
                                    (preview/clamp-start-ms track section-ms ms))
                           (restart-section-loop! store)))}
   {:ui.action/kind :music/confirm
    :ui.action/handler (fn [{:keys [store ui/event-handler]}]
                         (confirm! store event-handler))}])

;; --- modal ---

(defn render
  [state]
  [:div.flex.items-center.w-full.justify-center.flex-col
   [:div.flex.items-center.w-full
    (if (query/loading? state {:query/kind :query/ig-account-data})
      [:span "loading..."]
      [:div.w-full
       ;; Seed the modal state from the resolved account query once the
       ;; container mounts, then render the panel from the store:
       {:replicant/on-mount [[:music/init]]}
       (when (get-in state [:ui/modal :music])
         (music-modal/panel state))])]])

(def modal
  {:modal-id modal-id
   :on-load (fn [_params]
              [[:data/query {:query/kind :query/ig-account-data}]])
   ;; Stop the preview playback when the modal closes: its audio is not
   ;; part of the DOM, so that no unmount stops it:
   :on-unload (fn [_state]
                [[:music/stop-preview]])
   :render #'render})
